import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, PageBody, EmptyState } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Trash, ArrowUpRight } from "@phosphor-icons/react";
import { Badge } from "./Projects";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);
const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Invoices() {
  const location = useLocation();
  const { data: invoices = [] } = useSWR("/invoices", fetcher);
  const { data: projects = [] } = useSWR("/projects", fetcher);
  const [open, setOpen] = useState(false);
  const [projectId, setProjectId] = useState(location.state?.project_id || "");
  const [customer, setCustomer] = useState("");
  const [address, setAddress] = useState("");
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10);
  });
  const [tax, setTax] = useState(0);
  const [lines, setLines] = useState([{ label: "Roof installation", category: "labor", qty: 1, unit: "lot", unit_price: 5000, total: 5000 }]);

  const updateLine = (i, patch) => {
    const next = [...lines]; next[i] = { ...next[i], ...patch };
    next[i].total = Math.round(Number(next[i].qty || 0) * Number(next[i].unit_price || 0) * 100) / 100;
    setLines(next);
  };

  const pickProject = (id) => {
    setProjectId(id);
    const p = projects.find(x => x.id === id);
    if (p) { setCustomer(p.customer_name || ""); setAddress(p.address || ""); }
  };

  const subtotal = lines.reduce((a, b) => a + Number(b.total || 0), 0);
  const total = subtotal * (1 + tax / 100);

  const submit = async () => {
    if (!projectId || !customer || lines.length === 0) return toast.error("Project, customer & lines required");
    try {
      await api.post("/invoices", { project_id: projectId, customer_name: customer, address, lines, tax_pct: Number(tax), due_date: dueDate });
      toast.success("Invoice created");
      setOpen(false);
      mutate("/invoices");
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="invoices-page">
      <PageHeader
        eyebrow="// 06 / Billing"
        title="Invoices"
        sub="Draft, send, and collect payment via Stripe. Paid invoices auto-post to the ledger."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-invoice-button" className="rounded-none bg-zinc-900 hover:bg-orange-600 text-white uppercase font-bold tracking-wider text-xs h-11 px-5">
                <Plus size={14} className="mr-2" weight="bold" /> New Invoice
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-zinc-300 max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display font-black tracking-tight text-2xl">New Invoice</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 mt-4">
                <Field label="Project">
                  <Select value={projectId} onValueChange={pickProject}>
                    <SelectTrigger data-testid="inv-project-select" className="rounded-none"><SelectValue placeholder="Pick project…" /></SelectTrigger>
                    <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Due date"><Input data-testid="inv-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="rounded-none mono" /></Field>
                <Field label="Customer"><Input data-testid="inv-customer" value={customer} onChange={(e) => setCustomer(e.target.value)} className="rounded-none" /></Field>
                <Field label="Address"><Input value={address} onChange={(e) => setAddress(e.target.value)} className="rounded-none" /></Field>
              </div>
              <div className="mt-4 border border-zinc-200">
                <div className="grid grid-cols-12 px-3 py-2 border-b border-zinc-200 label-eyebrow text-zinc-500 bg-zinc-50">
                  <div className="col-span-5">Item</div>
                  <div className="col-span-2">Qty</div>
                  <div className="col-span-2">Unit</div>
                  <div className="col-span-2">Unit $</div>
                  <div className="col-span-1 text-right">Total</div>
                </div>
                {lines.map((l, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1 px-3 py-2 border-b border-zinc-100 items-center">
                    <Input data-testid={`inv-line-label-${i}`} className="col-span-5 rounded-none" value={l.label} onChange={(e) => updateLine(i, { label: e.target.value })} />
                    <Input type="number" className="col-span-2 rounded-none mono" value={l.qty} onChange={(e) => updateLine(i, { qty: e.target.value })} />
                    <Input className="col-span-2 rounded-none mono text-xs" value={l.unit} onChange={(e) => updateLine(i, { unit: e.target.value })} />
                    <Input type="number" step="0.01" className="col-span-2 rounded-none mono" value={l.unit_price} onChange={(e) => updateLine(i, { unit_price: e.target.value })} />
                    <div className="col-span-1 mono text-xs text-right flex items-center justify-end gap-1">
                      {fmt$(l.total)}
                      <button onClick={() => setLines(lines.filter((_, x) => x !== i))} className="text-zinc-400 hover:text-orange-600">
                        <Trash size={12} />
                      </button>
                    </div>
                  </div>
                ))}
                <div className="p-2">
                  <Button variant="outline" onClick={() => setLines([...lines, { label: "", category: "material", qty: 1, unit: "ea", unit_price: 0, total: 0 }])} className="rounded-none border-zinc-300 text-xs uppercase font-bold tracking-wider">
                    <Plus size={14} className="mr-2" /> Add line
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-4 items-end">
                <Field label="Tax %"><Input data-testid="inv-tax" type="number" value={tax} onChange={(e) => setTax(Number(e.target.value))} className="rounded-none mono" /></Field>
                <div className="text-right col-span-2">
                  <p className="label-eyebrow text-zinc-500">CUSTOMER TOTAL</p>
                  <p className="font-display font-black text-3xl text-orange-600 tracking-tighter">{fmt$(total)}</p>
                </div>
              </div>
              <Button data-testid="inv-save" onClick={submit} className="rounded-none bg-orange-600 hover:bg-orange-700 text-white uppercase font-bold tracking-wider w-full h-11 mt-4">Create invoice</Button>
            </DialogContent>
          </Dialog>
        }
      />
      <PageBody>
        {invoices.length === 0 ? (
          <EmptyState title="No invoices yet" sub="Convert your first estimate into a billable invoice." />
        ) : (
          <div className="border border-zinc-200">
            <div className="grid grid-cols-12 px-5 py-3 border-b border-zinc-200 label-eyebrow text-zinc-500 bg-zinc-50">
              <div className="col-span-2">Number</div>
              <div className="col-span-3">Customer</div>
              <div className="col-span-2">Issued</div>
              <div className="col-span-2">Due</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-2 text-right">Total</div>
            </div>
            {invoices.map(inv => (
              <Link key={inv.id} to={`/app/invoices/${inv.id}`} data-testid={`invoice-row-${inv.id}`}
                className="grid grid-cols-12 px-5 py-4 border-b border-zinc-100 hover:bg-zinc-50 items-center">
                <div className="col-span-2 mono text-sm font-semibold">{inv.invoice_number}</div>
                <div className="col-span-3 text-sm">{inv.customer_name}</div>
                <div className="col-span-2 mono text-xs text-zinc-600">{inv.issue_date}</div>
                <div className="col-span-2 mono text-xs text-zinc-600">{inv.due_date}</div>
                <div className="col-span-1"><Badge status={inv.status} /></div>
                <div className="col-span-2 text-right mono font-semibold flex items-center justify-end gap-2">
                  {fmt$(inv.total)} <ArrowUpRight size={14} className="text-zinc-400" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </PageBody>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="label-eyebrow text-zinc-600 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
